"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Calendar, Loader, CircleCheck } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
const minimalistInputClass = "border-0 border-b border-primary/20 rounded-none px-0 py-2 h-auto focus-visible:ring-0 focus-visible:border-primary shadow-none bg-transparent transition-colors text-base font-default";
const minimalistLabelClass = "text-xs uppercase tracking-widest text-muted-foreground mb-1 font-default";
function ReservationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="232856" className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in duration-700">
        <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="font-default text-3xl font-semibold tracking-wide text-foreground">
          Foglalás Elküldve
        </h3>
        <p className="text-muted-foreground max-w-md leading-relaxed font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-full border-primary/20 hover:bg-accent tracking-widest uppercase text-xs px-8 font-default">
          Új Foglalás Indítása
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8 py-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
        {}
        <FormField control={control} name="name" rules={{
        required: "A név megadása kötelező"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={minimalistLabelClass}>Teljes Név</FormLabel>
              <FormControl>
                <Input placeholder="Kovács János" className={minimalistInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-[10px] opacity-70 font-default">
                Kérjük, adja meg a foglaló teljes nevét.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        required: "Telefonszám megadása kötelező",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Érvénytelen formátum"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={minimalistLabelClass}>
                Telefonszám
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+36 30 123 4567" className={minimalistInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-[10px] opacity-70 font-default">
                Nemzetközi formátumban adja meg.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email cím megadása kötelező",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Érvénytelen email cím"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1 md:col-span-2">
              <FormLabel className={minimalistLabelClass}>
                Email Cím
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="pelda@travel.hu" className={minimalistInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="check_in_date" rules={{
        required: "Indulási dátum kötelező"
      }} render={({
        field
      }) => <FormItem className="space-y-1 flex flex-col">
              <FormLabel className={minimalistLabelClass}>
                Indulás / Érkezés
              </FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button variant="outline" className={cn(minimalistInputClass, "w-full justify-start text-left font-normal hover:bg-transparent hover:text-foreground", !field.value && "text-muted-foreground")}>
                      <SafeHydrate><Calendar className="mr-3 size-4 opacity-50" /></SafeHydrate>
                      {field.value ? new Date(field.value).toLocaleDateString("hu-HU", {
                  month: "long",
                  day: "numeric",
                  year: "numeric"
                }) : <span>Válasszon dátumot</span>}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <SafeHydrate><CalendarComponent mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date ? date.toISOString() : "")} initialFocus /></SafeHydrate>
                </PopoverContent>
              </Popover>
              <FormDescription className="text-[10px] opacity-70 font-default">
                Az utazás megkezdésének napja.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="check_out_date" rules={{
        required: "Visszaút dátuma kötelező"
      }} render={({
        field
      }) => <FormItem className="space-y-1 flex flex-col">
              <FormLabel className={minimalistLabelClass}>
                Visszaút / Távozás
              </FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button variant="outline" className={cn(minimalistInputClass, "w-full justify-start text-left font-normal hover:bg-transparent hover:text-foreground", !field.value && "text-muted-foreground")}>
                      <SafeHydrate><Calendar className="mr-3 size-4 opacity-50" /></SafeHydrate>
                      {field.value ? new Date(field.value).toLocaleDateString("hu-HU", {
                  month: "long",
                  day: "numeric",
                  year: "numeric"
                }) : <span>Válasszon dátumot</span>}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <SafeHydrate><CalendarComponent mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date ? date.toISOString() : "")} initialFocus /></SafeHydrate>
                </PopoverContent>
              </Popover>
              <FormDescription className="text-[10px] opacity-70 font-default">
                A hazautazás tervezett napja.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="guests" rules={{
        required: "Utasok száma kötelező",
        min: {
          value: 1,
          message: "Minimum 1 fő"
        },
        max: {
          value: 10,
          message: "Maximum 10 fő"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1 md:col-span-2">
              <FormLabel className={minimalistLabelClass}>
                Utasok Száma
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value?.toString()}>
                <FormControl>
                  <SelectTrigger className={cn(minimalistInputClass, "w-full hover:bg-transparent focus:ring-0")}>
                    <SelectValue placeholder="Válassza ki a létszámot" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => <SelectItem key={num} value={num.toString()}>
                      {num} {num === 1 ? "Fő" : "Fő"}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormDescription className="text-[10px] opacity-70 font-default">
                Összes utazó száma (gyermekekkel együtt).
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="message" render={({
        field
      }) => <FormItem className="space-y-1 md:col-span-2">
              <FormLabel className={minimalistLabelClass}>
                Megjegyzés / Különleges kérések
              </FormLabel>
              <FormControl>
                <Textarea placeholder="Taxi igény, speciális étrend, vagy egyéb kérés..." className={cn(minimalistInputClass, "min-h-[100px] resize-none")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-default rounded-lg">
          {submitError}
        </div>}

      <div className="pt-6 flex justify-end">
        <Button type="submit" disabled={isSubmitting} className="rounded-full px-12 py-6 bg-primary text-primary-foreground hover:bg-primary/90 tracking-widest uppercase text-xs transition-all shadow-lg font-default">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Feldolgozás...
            </> : "Foglalás Küldése"}
        </Button>
      </div>
    </Form>;
}
export default function ReservationForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [isOpen, setIsOpen] = useState(false);
  return;
}